<template>
  <div
    id="tabControl"
    class="row relative btn-row c-pt-7 c-mx-0 c-flex-nowrap overflow-auto w-100"
  >
    <button
      type="button"
      class="tab-btn font-family-secondary c-mb-0 c-py-2 c-px-4 tab-control-btn text-primary"
      :class="{ active: activeTab === 1 }"
      @click="
        () => {
          emit('changeActiveTab', 1)
        }
      "
    >
      {{ t('description') }}
    </button>
    <button
      type="button"
      class="tab-btn font-family-secondary c-mb-0 c-py-2 c-px-4 tab-control-btn text-primary"
      :class="{ active: activeTab === 2 }"
      @click="
        () => {
          emit('changeActiveTab', 2)
        }
      "
    >
      {{ t('composition') }} & {{ t('nutritional_information') }}
    </button>
    <button
      type="button"
      class="tab-btn font-family-secondary c-mb-0 c-py-2 c-px-4 tab-control-btn text-primary"
      :class="{ active: activeTab === 3 }"
      @click="
        () => {
          emit('changeActiveTab', 3)
        }
      "
    >
      {{ t('ratings') }}
      <span v-if="commentsSum > 0">({{ commentsSum }})</span>
    </button>
  </div>
  <div class="tab-control-tabs relative z-2 c-md:c-pl-5 c-md:c-pr-5 c-pl-0 c-pr-0">
    <div class="container c-mx-auto c-md:c-pt-6 c-pt-5 c-pb-6 c-lg:c-pl-0 c-pl-4 c-lg:c-pr-0 c-pr-4">
      <div
        v-if="activeTab === 1"
        class="tab"
      >
        <div class="row c-mx-0">
          <!-- Description + ean -->
          <div class="c-lg:col-6 col-12 c-pr-0 c-lg:c-pr-3 c-pl-0">
            <div class="text-sm-c">
              <p
                v-if="product.state === 'weather_too_hot'"
                class="font-bold text-info"
              >
                {{ t('selected_chocolate_products') }}
              </p>
              <p v-html="product.description" />
              <p
                v-if="product.tags"
                class="tags-row"
              >
                <em
                  v-for="(tag, index) in product.tags.split(', ')"
                  :key="index"
                  class="c-mr-1 inline-block"
                >
                  #{{ tag }}
                </em>
              </p>
            </div>
          </div>
          <div class="c-lg:col-6 col-12 c-pl-0 c-lg:c-pl-3 c-pr-0 c-mt-5 c-lg:c-mt-0">
            <ProductBenefitsList
              v-if="product.isReadyProduct"
              :benefits="productBenefits"
            />
          </div>
        </div>
      </div>
      <div
        v-if="activeTab === 2"
        class="tab"
      >
        <div class="row c-mx-0">
          <div class="c-lg:col-6 col-12 c-pr-0 c-lg:c-pr-3 c-pl-0">
            <ProductComposition
              :product="product"
              :with-title="false"
              :show-full-composition="true"
            />
          </div>
          <div class="c-lg:col-6 col-12 c-mt-5 c-lg:c-mt-0 c-pl-0 c-lg:c-pl-3 c-pr-0">
            <ProductPack
              v-if="product.productPack"
              :product="product"
            />
            <ProductNutritionTable
              v-if="product.showNutrition"
              :product="product"
              :is-full-width="true"
              :theme-light="true"
              section-classes="c-pt-0"
            />
          </div>
        </div>
      </div>
      <div
        v-if="activeTab === 3"
        class="tab"
      >
        <div class="flex c-justify-end c-mb-6">
          <div class="flex c-justify-end c-items-center">
            <strong class="c-mr-2">{{ t('sort_by') }}</strong>
            <select
              id="sort-type"
              v-model="sortByNewest"
              class="custom-select text-left c-rounded-sm w-auto"
              @change="emit('sortByNewest', sortByNewest)"
            >
              <option
                :value="false"
                selected
              >
                {{ t('sort_by_rating') }}
              </option>
              <option :value="true">
                {{ t('sort_by_newest') }}
              </option>
            </select>
          </div>
        </div>
        <ProductReviews
          v-if="topComments && comments"
          :product="product"
          :top-comments="topComments"
          :comments="comments"
        />
        <BaseLoading
          v-else
          :style="{ minHeight: '490px' }"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import ProductBenefitsList from '@/components/products/ProductBenefitsList.vue'
import ProductComposition from '@/components/products/ProductComposition.vue'
import ProductNutritionTable from '@/components/products/ProductNutritionTable.vue'
import ProductPack from '@/components/products/ProductPack.vue'
import ProductReviews from '@/components/products/ProductReviews.vue'
import BaseLoading from '@/components/general/BaseLoading.vue'

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  activeTab: {
    type: Number,
    required: true,
  },
  commentsSum: {
    type: Number,
    required: true,
  },
  topComments: {
    type: Array,
    required: true,
    default: () => [],
  },
  comments: {
    type: Array,
    required: true,
    default: () => [],
  },
})

const sortByNewest = ref(false);
const emit = defineEmits(['changeActiveTab', 'sortByNewest'])

const productBenefits = computed(() => {
  return props.product ? props.product.properties.filter((p) => p.propertyType === 'benefit') : []
})

const { t } = useI18n()
</script>

<style lang="scss">
.tab-control-btn {
  opacity: 0.7;
  border: none;
  outline: none;
  background-color: rgba(0, 0, 0, 0);
  border-top-left-radius: 22px;
  border-top-right-radius: 22px;
  box-shadow: 0px -2px 4px rgba(0, 0, 0, 0);
  transition: 0.5s box-shadow;

  &.active {
    opacity: 1;
    box-shadow: 0px -2px 4px rgba(0, 0, 0, 0.1);
    background-color: #f7e7d1;
  }
}
.tab-control-tabs {
  box-shadow: 0px -6px 7px rgba(0, 0, 0, 0.1);
}

.btn-row {
  z-index: 4;
  justify-content: center;
}

.tab-btn {
  font-weight: 800;
  font-size: 20px;
}

@media screen and (max-width: 991px) {
  .tab-control-btn {
    &.active {
      background-color: $white;
    }
  }
}

@media screen and (max-width: 650px) {
  .btn-row {
    justify-content: c-flex-start;
    padding-left: 6px;
    padding-right: 6px;

    &::-webkit-scrollbar {
      height: 4px;
    }
    &::-webkit-scrollbar-track {
      background: $white;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(79, 43, 27, 0.4);
      border-radius: 50rem;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: $primary-color-800;
    }
  }
  .tab-btn {
    font-size: 16px;
  }
}
</style>

<i18n>
  {
    "cs": {
    "composition": "Složení",
    "description": "Popis",
    "nutritional_information": "Výživové údaje",
    "ratings": "Hodnocení",
    "selected_chocolate_products": "Jsem u ledu! Z důvodu vysokých teplot mě poslali do chládku.",
    "sort_by": "Řazení",
    "sort_by_newest": "Od nejnovějších",
    "sort_by_rating": "Dle hodnocení"
},
    "sk": {
    "composition": "Zloženie",
    "description": "Popis",
    "nutritional_information": "Výživové údaje",
    "ratings": "Hodnotenie",
    "selected_chocolate_products": "Som v chládku! :( Čokoláda má kvôli vysokým teplotám dovolenku.",
    "sort_by": "Triedenie",
    "sort_by_newest": "Zoradiť podľa najnovších",
    "sort_by_rating": "Podľa hodnotenia"
},
    "pl": {
    "composition": "Skład",
    "description": "Opis",
    "nutritional_information": "Wartości odżywcze",
    "ratings": "Ocena",
    "selected_chocolate_products": "Czekam w chłodzie! :( Ze względu na upały, czekolada została wysłana na przymusowy urlop.",
    "sort_by": "Sortowanie",
    "sort_by_newest": "Sortuj według najnowszych",
    "sort_by_rating": "Według oceny"
},
    "hu": {
    "composition": "Összetevők",
    "description": "Leírás",
    "nutritional_information": "Táplálkozási adatok",
    "ratings": "Értékelés",
    "selected_chocolate_products": "Hidegre helyezve! :( A magas hőmérsékletek miatt a csokoládés termékek szabadságon vannak.",
    "sort_by": "Rendezés",
    "sort_by_newest": "Legújabb szerint rendezés",
    "sort_by_rating": "Értékelés szerint"
},
    "de": {
    "composition": "Zusammensetzung:",
    "description": "Beschreibung",
    "nutritional_information": "Nährwertangaben",
    "ratings": "Bewertung",
    "selected_chocolate_products": "Ich liege auf Eis! :( Wegen der hohen Temperaturen haben sie mich in die Kühlung geschickt.",
    "sort_by": "Sortierung",
    "sort_by_newest": "Nach den neuesten sortieren",
    "sort_by_rating": "Nach Bewertung"
},
    "at": {
    "composition": "Zusammensetzung:",
    "description": "Beschreibung",
    "nutritional_information": "Nährwertangaben",
    "ratings": "Bewertung",
    "selected_chocolate_products": "Ich liege auf Eis! :( Wegen der hohen Temperaturen haben sie mich in die Kühlung geschickt.",
    "sort_by": "Sortierung",
    "sort_by_newest": "Nach den neuesten sortieren",
    "sort_by_rating": "Nach Bewertung"
},
    "ro": {
    "composition": "Ingrediente",
    "description": "Descriere",
    "nutritional_information": "Informații nutriționale",
    "ratings": "Evaluare",
    "selected_chocolate_products": "La rece! :( Din cauza temperaturilor înalte produsele cu ciocolată sunt în concediu.",
    "sort_by": "Sortare",
    "sort_by_newest": "Sortează după cele mai recente",
    "sort_by_rating": "Conform evaluării"
},
    "fr": {
    "composition": "Composition",
    "description": "Description",
    "nutritional_information": "Valeurs nutritionnelles",
    "ratings": "Avis des clients",
    "selected_chocolate_products": "Je suis au frais ! À cause des températures élevées, ils m'ont plongée dans le froid.",
    "sort_by": "Trier par",
    "sort_by_newest": "Trier par nouveauté",
    "sort_by_rating": "Selon la note"
},
    "it": {
    "composition": "Ingredienti",
    "description": "Descrizione",
    "nutritional_information": "Valori nutrizionali",
    "ratings": "Recensione",
    "selected_chocolate_products": "Sono sotto ghiaccio! A causa delle alte temperature mi hanno mandato in spedizione polare per raffreddarmi.",
    "sort_by": "Ordina per",
    "sort_by_newest": "I più recenti",
    "sort_by_rating": "Secondo la valutazione"
}
  }
</i18n>
